import type {
  AskGtmPlaySurface,
  AskPageContext,
  ChatTurnPageContext,
} from "../types/chat.types";

const GTM_PLAY_PATH =
  /^\/workspaces\/[^/]+\/gtm\/leads\/plays\/([^/]+)\/?$/;
const GTM_BRAIN_PATH = /^\/workspaces\/[^/]+\/gtm\/leads\/brain\/?$/;
const GTM_SHELL_PATH = /^\/workspaces\/[^/]+\/gtm\/leads(?:\/.*)?$/;

const SURFACE_LABELS: Record<AskGtmPlaySurface, string> = {
  overview: "Overview",
  leads: "Leads",
  sequence: "Sequence",
};

/** True when ⌘K should not toggle Ask (focus in editable / Quill). */
export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const el = target instanceof HTMLElement ? target : target.parentElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el instanceof HTMLElement) {
    const ce = el.contentEditable;
    if (ce === "true" || ce === "plaintext-only") return true;
  }
  const attr = el.getAttribute("contenteditable");
  if (attr === "" || attr === "true" || attr === "plaintext-only") return true;
  if (el.closest(".ql-editor, [contenteditable='true'], [contenteditable='']")) return true;
  return false;
}

export function parseAskGtmPlaySurface(raw: string | null | undefined): AskGtmPlaySurface {
  if (raw === "leads" || raw === "sequence" || raw === "overview") return raw;
  return "overview";
}

export interface DeriveAskPageContextInput {
  pathname: string;
  search: string;
  /** Play display name from cache when known. */
  playName?: string | null;
}

/**
 * Pure route → AskPageContext mapping (story 12.1).
 * Play surface comes from `?tab=` (synced by GtmPlayDetailPage).
 */
export function deriveAskPageContext({
  pathname,
  search,
  playName,
}: DeriveAskPageContextInput): AskPageContext {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (GTM_BRAIN_PATH.test(path)) {
    return { kind: "gtm-brain" };
  }

  const playMatch = path.match(GTM_PLAY_PATH);
  if (playMatch) {
    const playId = playMatch[1] ?? "";
    const tab = new URLSearchParams(search).get("tab");
    const surface = parseAskGtmPlaySurface(tab);
    return {
      kind: "gtm-play",
      playId,
      playName: (playName && playName.trim()) || playId,
      surface,
    };
  }

  if (GTM_SHELL_PATH.test(path)) {
    return { kind: "workspace" };
  }

  return { kind: "workspace" };
}

const GTM_LEADS_PATH = /\/workspaces\/[^/]+\/gtm\/leads/;

/**
 * Merge route context with published CRM detail context (story 12.5).
 * GTM routes always win; CRM record only applies off GTM.
 */
export function mergeAskPageContext(input: {
  pathname: string;
  routeContext: AskPageContext;
  crmRecord: Extract<AskPageContext, { kind: "crm-record" }> | null;
}): AskPageContext {
  const path = input.pathname.replace(/\/+$/, "") || "/";
  if (GTM_LEADS_PATH.test(path)) {
    return input.routeContext;
  }
  if (input.crmRecord) {
    return input.crmRecord;
  }
  return input.routeContext;
}

/** Map UI AskPageContext → wire pageContext (crm-record uses recordContext instead). */
export function toWirePageContext(ctx: AskPageContext): ChatTurnPageContext | undefined {
  switch (ctx.kind) {
    case "gtm-play":
      return {
        scope: "gtm-play",
        playId: ctx.playId,
        playName: ctx.playName,
        surface: ctx.surface,
      };
    case "gtm-brain":
      return { scope: "gtm-brain" };
    case "workspace":
      return { scope: "workspace" };
    case "crm-record":
      return undefined;
    default: {
      const _exhaustive: never = ctx;
      return _exhaustive;
    }
  }
}

/** Chip primary + secondary labels for non-CRM page context. */
export function askPageContextChipLabel(ctx: AskPageContext): {
  label: string;
  sublabel?: string;
} {
  switch (ctx.kind) {
    case "gtm-play":
      return {
        label: ctx.playName,
        sublabel: SURFACE_LABELS[ctx.surface],
      };
    case "gtm-brain":
      return { label: "GTM Brain" };
    case "crm-record":
      return {
        label: ctx.displayName ?? ctx.recordId,
        sublabel: ctx.subtitle ?? ctx.recordType,
      };
    case "workspace":
    default:
      return { label: "Workspace" };
  }
}
